import { useCallback, useEffect, useRef, useState } from 'react';
import { US_STATES, isPlausibleApiKey, tenantReadiness, type Tenant, type TenantIssue } from '@ww/shared';
import { api, type TenantInput, type ZoneDerivation } from '../api';
import { errorMessage } from '../lib/status';
import { slugify } from '../lib/text';
import AreaPicker, { type AreaValue } from './AreaPicker';
import { ErrorBox, PasswordField } from './ui';

const EMPTY_AREA: AreaValue = { area: null, boundary: null, center: null };

const STEPS = [
  { n: 1, label: 'State' },
  { n: 2, label: 'Coverage area' },
  { n: 3, label: 'Details' },
] as const;

interface Details {
  name: string;
  slug: string;
  countyName: string;
  lat: string;
  lon: string;
  timezone: string;
  zoneCodes: string[];
}

const EMPTY_DETAILS: Details = {
  name: '',
  slug: '',
  countyName: '',
  lat: '',
  lon: '',
  timezone: 'America/Chicago',
  zoneCodes: [],
};

const ZONE_RE = /^[A-Z]{2}[CZ]\d{3}$/;

export interface TenantWizardProps {
  onCreated: (tenant: Tenant, issues: TenantIssue[]) => void;
  onCancel: () => void;
}

export default function TenantWizard({ onCreated, onCancel }: TenantWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [state, setState] = useState('');
  const [area, setArea] = useState<AreaValue>(EMPTY_AREA);
  const [d, setD] = useState<Details>(EMPTY_DETAILS);
  const [derivation, setDerivation] = useState<ZoneDerivation | null>(null);
  const [deriving, setDeriving] = useState(false);
  const [zoneInput, setZoneInput] = useState('');
  const [withAdmin, setWithAdmin] = useState(true);
  // Optional here: a tenant can be created now and its key pasted later on the SMS
  // gateway tab. Kept out of `Details` so a re-derive can never touch it.
  const [smsKey, setSmsKey] = useState('');
  const [admin, setAdmin] = useState({ name: '', username: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fields the operator has edited by hand are never overwritten by a re-derive.
  const touched = useRef(new Set<keyof Details>());
  const prefilledFor = useRef<string | null>(null);

  const set = (patch: Partial<Details>) => {
    (Object.keys(patch) as Array<keyof Details>).forEach((k) => touched.current.add(k));
    setD((x) => ({ ...x, ...patch }));
  };

  const prefill = useCallback(async () => {
    if (!area.area || !area.center) return;
    const [lon, lat] = area.center;
    const label = area.area.label;
    const round = (n: number) => String(Math.round(n * 1e5) / 1e5);
    const base: Partial<Details> = {};
    if (!touched.current.has('name')) base.name = `${label}, ${state}`;
    if (!touched.current.has('slug')) base.slug = slugify(`${label}-${state}`);
    if (!touched.current.has('countyName')) base.countyName = area.area.kind === 'county' ? label : '';
    if (!touched.current.has('lat')) base.lat = round(lat);
    if (!touched.current.has('lon')) base.lon = round(lon);
    setD((x) => ({ ...x, ...base }));

    setDeriving(true);
    setError(null);
    try {
      const dv = await api.deriveZones({ state, kind: area.area.kind, name: label, lon, lat });
      setDerivation(dv);
      setD((x) => ({
        ...x,
        zoneCodes: touched.current.has('zoneCodes') ? x.zoneCodes : dv.zoneCodes,
        timezone: touched.current.has('timezone') ? x.timezone : dv.timezone || x.timezone,
        countyName:
          x.countyName ||
          (dv.zones.find((z) => z.type === 'county')?.name
            ? `${dv.zones.find((z) => z.type === 'county')!.name} County`
            : ''),
      }));
    } catch (err) {
      // Never block creation on a derivation failure — the codes stay editable.
      setError(`Could not look up NWS zone codes: ${errorMessage(err)}`);
    } finally {
      setDeriving(false);
    }
  }, [area.area, area.center, state]);

  useEffect(() => {
    if (step !== 3 || !area.area) return;
    const key = `${state}:${area.area.kind}:${area.area.geoid ?? area.area.label}`;
    if (prefilledFor.current === key) return;
    prefilledFor.current = key;
    void prefill();
  }, [step, state, area.area, prefill]);

  const addZones = (raw: string) => {
    const next = raw
      .split(/[,\s]+/)
      .map((z) => z.trim().toUpperCase())
      .filter(Boolean);
    const bad = next.filter((z) => !ZONE_RE.test(z));
    if (bad.length) {
      setError(`Not a zone code: ${bad.join(', ')}. They look like ARC085 or ARZ045.`);
      return;
    }
    set({ zoneCodes: Array.from(new Set([...d.zoneCodes, ...next])).sort() });
    setZoneInput('');
    setError(null);
  };

  const draftIssues: TenantIssue[] = tenantReadiness(
    { zoneCodes: d.zoneCodes, boundary: area.boundary, notificationGroups: [], alertPolicy: null },
    { users: withAdmin ? 1 : 0 },
  );

  const submit = async () => {
    const lat = Number(d.lat);
    const lon = Number(d.lon);
    if (!d.name.trim() || !d.slug.trim() || !d.countyName.trim())
      return setError('Name, slug and county name are required.');
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180)
      return setError('Enter a valid map centre.');
    if (!area.area || !area.boundary) return setError('Choose a coverage area.');
    if (withAdmin && (!admin.name.trim() || !admin.username.trim() || !admin.email.trim() || admin.password.length < 8))
      return setError('The first admin needs a name, username, email and a password of at least 8 characters.');
    if (smsKey.trim() && !isPlausibleApiKey(smsKey))
      return setError('That does not look like an Easy SMS Relay API key — paste the key only, with no spaces.');

    const body: TenantInput = {
      name: d.name.trim(),
      slug: d.slug.trim(),
      state,
      countyName: d.countyName.trim(),
      zoneCodes: d.zoneCodes,
      center: [lon, lat],
      timezone: d.timezone.trim() || 'America/Chicago',
      boundary: area.boundary,
      area: area.area,
      ...(withAdmin ? { firstAdmin: { ...admin, name: admin.name.trim(), username: admin.username.trim() } } : {}),
      ...(smsKey.trim() ? { sms: { provider: 'easysmsrelay' as const, apiKey: smsKey.trim() } } : {}),
    };
    setBusy(true);
    setError(null);
    try {
      const res = await api.createTenant(body);
      onCreated(res.tenant, res.issues);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const canNext = step === 1 ? !!state : step === 2 ? !!(area.area && area.boundary) : false;

  return (
    <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, flexGrow: 1 }}>New tenant</h2>
        <button type="button" className="btn-link hov" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <nav className="stepper" aria-label="Create tenant">
        {STEPS.map((s, i) => (
          <span key={s.n} style={{ display: 'contents' }}>
            {i > 0 && <span className="step-sep" aria-hidden="true" />}
            <button
              type="button"
              className="step"
              aria-current={step === s.n ? 'step' : undefined}
              data-done={step > s.n ? 'true' : undefined}
              disabled={s.n > step}
              onClick={() => s.n < step && setStep(s.n as 1 | 2 | 3)}
            >
              <span className="step-num">{s.n}</span>
              {s.label}
            </button>
          </span>
        ))}
      </nav>

      <ErrorBox error={error} />

      {step === 1 && (
        <>
          <label className="field">
            <span className="lbl">State</span>
            <select
              className="fld"
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                setArea(EMPTY_AREA);
                prefilledFor.current = null;
              }}
            >
              <option value="">Select a state…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <p className="muted" style={{ fontSize: 13, margin: 0, maxWidth: '62ch' }}>
            The state scopes the area search and decides which NWS feed the worker polls for this
            tenant. Adding the first tenant in a new state starts that feed automatically.
          </p>
        </>
      )}

      {step === 2 && (
        <AreaPicker state={state} value={area} onChange={setArea} mapHeight={360} />
      )}

      {step === 3 && (
        <>
          <div className="grid-2">
            <label className="field">
              <span className="lbl">Name</span>
              <input className="fld" value={d.name} onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="field">
              <span className="lbl">Slug</span>
              <input className="fld mono" value={d.slug} onChange={(e) => set({ slug: e.target.value })} />
            </label>
          </div>
          <div className="grid-2">
            <label className="field">
              <span className="lbl">County name</span>
              <input className="fld" value={d.countyName} onChange={(e) => set({ countyName: e.target.value })} />
            </label>
            <label className="field">
              <span className="lbl">Timezone</span>
              <input className="fld mono" value={d.timezone} onChange={(e) => set({ timezone: e.target.value })} />
            </label>
          </div>
          <div className="grid-2">
            <label className="field">
              <span className="lbl">Centre latitude</span>
              <input className="fld mono" value={d.lat} onChange={(e) => set({ lat: e.target.value })} />
            </label>
            <label className="field">
              <span className="lbl">Centre longitude</span>
              <input className="fld mono" value={d.lon} onChange={(e) => set({ lon: e.target.value })} />
            </label>
          </div>

          <div className="field">
            <span className="lbl">
              NWS zone codes{' '}
              {deriving ? (
                <span className="muted">· looking up…</span>
              ) : derivation ? (
                <span className="muted">· derived from {derivation.source.replace('+', ' + ')}</span>
              ) : null}
            </span>
            <div className="zone-chips">
              {d.zoneCodes.map((z) => (
                <span key={z} className="chip mono">
                  {z}
                  <button
                    type="button"
                    className="btn-link hov"
                    aria-label={`Remove ${z}`}
                    onClick={() => set({ zoneCodes: d.zoneCodes.filter((x) => x !== z) })}
                  >
                    ×
                  </button>
                </span>
              ))}
              {!d.zoneCodes.length && !deriving && <span className="muted">none</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="fld mono"
                placeholder="ARC085, ARZ045"
                value={zoneInput}
                onChange={(e) => setZoneInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addZones(zoneInput);
                  }
                }}
              />
              <button type="button" className="btn btn-ghost btn-sm hov" onClick={() => addZones(zoneInput)}>
                Add
              </button>
              <button type="button" className="btn btn-ghost btn-sm hov" disabled={deriving} onClick={() => void prefill()}>
                Re-derive
              </button>
            </div>
            {derivation?.notes.map((n) => (
              <span key={n} className="muted" style={{ fontSize: 12.5 }}>
                {n}
              </span>
            ))}
          </div>

          <label className="field">
            <span className="lbl">Easy SMS Relay API key (optional)</span>
            <input
              className="fld mono"
              type="password"
              autoComplete="off"
              placeholder="relay_… — this tenant's own key"
              value={smsKey}
              onChange={(e) => setSmsKey(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 12.5 }}>
              Each tenant texts with its own key. Leave blank to add it later under Alert rules → SMS gateway.
            </span>
          </label>

          <label className="check" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={withAdmin} onChange={(e) => setWithAdmin(e.target.checked)} />
            <span>Create the first admin login for this tenant</span>
          </label>
          {withAdmin && (
            <div className="grid-2">
              <label className="field">
                <span className="lbl">Full name</span>
                <input className="fld" value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} />
              </label>
              <label className="field">
                <span className="lbl">Username</span>
                <input
                  className="fld mono"
                  value={admin.username}
                  onChange={(e) => setAdmin({ ...admin, username: e.target.value })}
                />
              </label>
              <label className="field">
                <span className="lbl">Email</span>
                <input
                  className="fld"
                  type="email"
                  value={admin.email}
                  onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
                />
              </label>
              <PasswordField
                label="Temporary password"
                autoComplete="new-password"
                value={admin.password}
                onChange={(v) => setAdmin({ ...admin, password: v })}
              />
            </div>
          )}

          {draftIssues.length > 0 && (
            <div className="warn-box">
              <strong>Before this tenant is fully live:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {draftIssues.map((i) => (
                  <li key={i.code}>{i.message}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="wizard-actions">
        {step > 1 && (
          <button type="button" className="btn btn-ghost hov" onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}>
            Back
          </button>
        )}
        {step < 3 ? (
          <button
            type="button"
            className="btn btn-primary hov"
            disabled={!canNext}
            onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
          >
            Next
          </button>
        ) : (
          <button type="button" className="btn btn-primary hov" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Creating…' : 'Create tenant'}
          </button>
        )}
      </div>
    </section>
  );
}
